import appIcon from '@assets/app-icon.png';
import { PRODUCT_NAME } from '@lib/customization/reportBranding';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { LocalizationManager, useLocalization } from '@lib/localization';
import { resolveLabel, resolveLabelEntry } from '@store/api/poiTypeEndpoints';
import { type ReportDesignTheme } from '@store/api/reportDesignEndpoints';
import { useMemo, useState } from 'react';
import { type DesignImages } from '../ImageField';
import { useDesignPicture } from '../useDesignPicture';
import { fillPlaceholders, palette, placeholdersOf, type Print } from './print';
import { sampleOf, summaryOf } from './sample';
import { useReaderBrand } from './useReaderBrand';

/** A date or a number in the reader's own writing; in the runtime's where it does not take the language tag. */
const inLanguage = <T,>(language: string, format: (value: T, tag: string | undefined) => string) => (value: T): string => {
  try {
    return format(value, language);
  } catch (_) {
    return format(value, undefined);
  }
};

/**
 * What the canvas draws a design from, worked out once for all its sheets: the sample
 * inspection in the interface language, the design's theme, and the two ways a text reaches
 * a page. The language is the interface's own: the PDF tab has a picker of its own for the
 * language a report is drawn in.
 *
 * Authored text is resolved as the server resolves it for that language: the reader's, its
 * base, the server's primary customization language, then any language that has text.
 */
export const usePrint = (theme: ReportDesignTheme, images: DesignImages): Print => {
  const { t, currentLanguage: language } = useLocalization();
  const locales = useCustomizationLocales();
  const { primary } = locales;

  // The moment a report would say it was made: once a visit, so the sheets do not disagree.
  const [generatedAt] = useState(() => new Date());
  const logo = useDesignPicture(images, theme.logo?.imageId);
  const brand = useReaderBrand();

  const base = useMemo(() => {
    const sample = sampleOf(language);
    const formatDate = inLanguage<Date>(language, (date, tag) => date.toLocaleDateString(tag));

    return {
      language,
      sample,
      summary: summaryOf(sample),
      generatedAt,
      formatDate,
      formatDateTime: inLanguage<Date>(language, (date, tag) => date.toLocaleString(tag)),
      formatNumber: inLanguage<number>(language, (value, tag) => value.toLocaleString(tag, { useGrouping: false, maximumFractionDigits: 20 })),
    };
  }, [language, generatedAt]);

  return useMemo((): Print => {
    const placeholders = placeholdersOf(base);

    const text: Print['text'] = (value) => {
      const entry = resolveLabelEntry(value ?? undefined, { primary, preferred: language });

      return entry && { locale: entry.locale, text: fillPlaceholders(entry.text, placeholders) };
    };

    return {
      ...base,
      accent: theme.accentColor || palette.accent,
      // The design's own, then the reader's brand, then the product's, as on a report. A
      // brand's name is plain: no placeholder in it is filled.
      brandName: text(theme.brandName)?.text
        ?? resolveLabelEntry(brand.brandName, { primary, preferred: language })?.text
        ?? PRODUCT_NAME,
      // A logo of the design that is gone or cannot be read gives way like one not set.
      logo: (theme.logo && (logo.picture ?? (logo.reading ? '' : undefined)))
        ?? brand.logo
        ?? (brand.logoReading ? '' : appIcon),
      images,
      t,
      translate: (key, values) => {
        const found = LocalizationManager.i18n.t(key, { returnDetails: true });

        // The language the string was found in: the reader's, or English where theirs lacks
        // the key. A key no table has is drawn as itself, and cased as English, as on a report.
        return { text: t(key, values), locale: found.res === key ? 'en' : found.usedLng };
      },
      text,
      label: (value) => resolveLabel(value, { primary, preferred: language }),
    };
  }, [base, theme, images, logo.picture, logo.reading, brand, primary, language, t]);
};
